export const sellerGuideContent = {
  title: 'The Mahj Market Seller Guide',
  subtitle: 'Welcome to The Mahj Market',
  metaTitle: 'Seller Guide | The Mahj Market',
  metaDescription:
    'A practical seller guide for The Mahj Market covering fees, listing tools, shipping, payouts, seller responsibilities, and intellectual property standards.',
  introduction: [
    'The Mahj Market is a curated marketplace built specifically for the Mahjong community. Our goal is simple: help Mahjong sellers and brands reach the right customers while keeping selling easy, secure, and affordable.',
    'Whether you are a casual player who wants to sell so you can buy something new or you create Mahjong tiles and accessories, The Mahj Market provides a dedicated place where Mahjong lovers come to shop.',
  ],
  sections: [
    {
      heading: 'Seller Fees Overview',
      paragraphs: [
        'The Mahj Market keeps pricing simple and transparent.',
        'Listing fee:',
        'The Mahj Market charges a flat 8% commission on each completed sale.',
        'There are no listing fees and no monthly seller subscription required.',
        'You only pay when you make a sale.',
        'Example:',
      ],
      table: {
        headers: ['Item Price', 'Mahj Market Fee (8%)', 'Seller Receives'],
        rows: [
          ['$20', '$1.60', '$18.40'],
          ['$50', '$4.00', '$46.00'],
          ['$100', '$8.00', '$92.00'],
          ['$200', '$16.00', '$184.00'],
        ],
      },
    },
    {
      heading: 'What Your 8% Includes',
      paragraphs: [
        'The Mahj Market fee supports a full-service marketplace designed specifically for Mahjong sellers.',
      ],
      subsections: [
        {
          title: 'Mahjong-Specific Customer Audience',
          paragraphs: [
            'The Mahj Market is built for Mahjong players, meaning your products are shown directly to a highly targeted audience already interested in Mahjong.',
          ],
        },
        {
          title: 'Marketplace Marketing',
          paragraphs: ['Your items benefit from collective marketing efforts including:'],
          listItems: [
            'Mahjong event promotions',
            'Social media campaigns',
            'Email marketing to Mahjong players',
            'Community partnerships and Mahjong leagues',
            'Influencer collaborations within the Mahjong community',
          ],
        },
        {
          title: 'Seller Profile or Storefront',
          paragraphs: ['Each seller receives their own space within The Mahj Market including:'],
          listItems: [
            'Seller profile',
            'Brand story section',
            'Product catalog',
            'Sharable links to your items',
            'Easy shop sharing',
          ],
        },
        {
          title: 'Easy Product Listing',
          paragraphs: ['Listing items is simple and fast:'],
          listItems: [
            'Create your profile, including setting up your banking info with Stripe so you can get paid.',
            'Add your items, including specific descriptions so buyers know what you are selling.',
            'Add photos. A minimum of 3 photos is required, and the more the better.',
            'Set your price and list.',
          ],
        },
        {
          title: 'Secure Payments',
          paragraphs: ['The Mahj Market provides:'],
          listItems: [
            'Secure checkout with Stripe. TMM does not take or keep any of your personal information; all information required by Stripe follows KYC, or Know Your Customer, requirements.',
            'Fraud protection. By asking for your banking info, Stripe helps take fraud out of the equation.',
            'Payment processing integration. Payment is received through Stripe once the buyer receives and accepts the product; this happens within 7 business days after shipping.',
          ],
        },
        {
          title: 'Order Management',
          paragraphs: ['Your dashboard allows you to:'],
          listItems: [
            'View incoming orders',
            'Track sales',
            'Manage inventory',
            'Communicate with customers',
          ],
        },
        {
          title: 'Customer Discovery',
          paragraphs: ['The Mahj Market helps shoppers find your products through:'],
          listItems: [
            'Category browsing',
            'Mahjong-themed collections',
            'Featured products',
            'Search functionality',
          ],
        },
        {
          title: 'Community Exposure',
          paragraphs: [
            'The Mahj Market is connected to a Mahjong social media influencer with a large following, many instructors, leagues, and event sponsorships, giving sellers opportunities for:',
          ],
          listItems: [
            'Event features',
            'Vendor spotlights',
            'Product showcases',
            'Mahjong community promotion',
          ],
        },
      ],
    },
    {
      heading: 'What Sellers Are Responsible For',
      paragraphs: ['To keep The Mahj Market efficient and seller-friendly, sellers manage:'],
      listItems: [
        'Shipping and fulfillment. The site creates and emails the label directly to you so you can print it, attach it to your box, and drop it at your closest UPS store.',
        'Marking your package as shipped. The system will notify the buyer and send them the tracking information.',
        'Payout timing. Stripe holds all money until the package is received. The system allows 7 business days from the ship date for this to happen. After 7 business days, you will automatically receive a payout.',
        'Bank processing time. Once Stripe pays out, your bank may take 5-17 days to process the payment and post it to your account.',
      ],
    },
    {
      heading: 'Seller Best Practices',
      paragraphs: ['Successful sellers typically:'],
      listItems: [
        'Use clear photos. The more the better: include more angles, tiles separated into suits, the box and all accessories, and any visible flaws pointed out.',
        'Include detailed descriptions and dimensions.',
        'Price to sell. Typically, resale items are marked at least 20-30% below retail.',
      ],
    },
    {
      heading: 'Why Sellers Love The Mahj Market',
      paragraphs: [
        'The Mahj Market was built specifically for the Mahjong community, giving sellers:',
      ],
      listItems: [
        'A targeted customer base',
        'Lower fees than many marketplaces',
        'Guaranteed payout',
        'Mahjong-focused marketing',
        'A community-driven platform',
      ],
    },
    {
      heading: 'Getting Started',
      paragraphs: ['Opening your shop takes just a few steps:'],
      orderedListItems: [
        'Create your seller account. All financial information is given to Stripe to set up your payout account. Stripe will ask for your Social Security number, which is part of U.S. law under the Patriot Act. The Mahj Market does not receive any of this information; it is all secure through Stripe.',
        'Set up your shop profile. The more information about you as a seller the better: upload a photo or logo and tell the buyer about yourself. If you are an individual with items to sell, use your own name as the store name. You do not have to be a store or an instructor to sell. You can sell 1 item or a thousand.',
        'Upload your first product.',
        'Start selling to the Mahjong community.',
      ],
    },
    {
      heading: 'Intellectual Property and Design Authenticity Policy',
      paragraphs: [
        'The Mahj Market is committed to maintaining a trusted, high-quality marketplace where originality and authenticity are respected. Sellers are expected to only list products that they have the legal right to sell.',
      ],
      subsections: [
        {
          title: 'Prohibited Listings',
          paragraphs: ['Sellers may not list any item that:'],
          listItems: [
            "Copies or closely imitates another brand's original design, artwork, or product.",
            'Uses protected logos, trademarks, or brand names without authorization.',
            'Is a "dupe," replica, knockoff, or otherwise intentionally designed to resemble a protected product.',
            'Incorporates copyrighted patterns, artwork, or proprietary designs without the permission of the rights holder.',
          ],
          closingParagraphs: [
            'All sellers are responsible for ensuring that their listings do not infringe on any third-party intellectual property rights. By listing on The Mahj Market, you confirm that your products are original designs and you have the right to sell them.',
          ],
        },
        {
          title: 'Enforcement',
          paragraphs: ['The Mahj Market reserves the right to:'],
          listItems: [
            'Remove any listing that appears to violate intellectual property laws or platform standards.',
            'Suspend or permanently remove seller accounts involved in repeated or serious violations.',
          ],
        },
      ],
    },
  ],
};

export default sellerGuideContent;
